import { useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function usePageTitle(title: string) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `Kraphene | ${title}`;

    return () => {
      document.title = previousTitle;
    };
  }, [title]);
}

export function useDrawerParam(paramKey = 'drawer', extraKeys: string[] = []) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const id = search[paramKey] ?? null;
  const extra = Object.fromEntries(extraKeys.map((key) => [key, search[key] ?? null]));

  const open = (value: string, extraValues?: Record<string, string>) =>
    navigate({
      search: (prev: Record<string, unknown>) => ({ ...prev, [paramKey]: value, ...extraValues }),
      viewTransition: false,
    } as never);

  const close = () =>
    navigate({
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        delete next[paramKey];
        for (const key of extraKeys) delete next[key];
        return next;
      },
      viewTransition: false,
    } as never);

  return { id, extra, open, close };
}